import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Typography, Space, Button, Select } from 'antd';
import { CheckSquareOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import '../../order/orders/Orders.css';
import './MyRewards.css';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;
const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

export default function MyRewards({ embedded = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [balance, setBalance] = useState(null);
  const [summaryYear, setSummaryYear] = useState(String(currentYear));
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });

  const fetchSummary = useCallback(
    async (year) => {
      setSummaryLoading(true);
      try {
        const res = await axios.get('/rewards/me/summary', { params: { year: year || summaryYear } });
        setSummary(res.data?.summary ?? {});
        setBalance(res.data?.balance ?? 0);
      } catch (err) {
        dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load summary', type: 'error' }));
        setSummary({});
        setBalance(null);
      } finally {
        setSummaryLoading(false);
      }
    },
    [dispatch, summaryYear]
  );

  const fetchList = useCallback(
    async (page, limit) => {
      setListLoading(true);
      try {
        const res = await axios.get('/rewards/me', { params: { page, limit } });
        setList(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load', type: 'error' }));
        setList([]);
      } finally {
        setListLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchSummary(summaryYear);
  }, [summaryYear, fetchSummary]);

  useEffect(() => {
    fetchList(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchList]);

  const monthLabels = {
    '01': 'January',
    '02': 'February',
    '03': 'March',
    '04': 'April',
    '05': 'May',
    '06': 'June',
    '07': 'July',
    '08': 'August',
    '09': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
  };

  // All 12 months for selected year; use 0 when no rewards; display never negative in card
  const allMonthsForYear = Array.from({ length: 12 }, (_, i) => {
    const mm = String(i + 1).padStart(2, '0');
    const monthKey = `${summaryYear}-${mm}`;
    const rawPoints = summary[monthKey] ?? 0;
    return { monthKey, monthLabel: monthLabels[mm] || mm, points: rawPoints, displayPoints: Math.max(0, rawPoints) };
  });
  const yearTotalDisplay = allMonthsForYear.reduce((acc, { displayPoints }) => acc + displayPoints, 0);

  return (
    <div className="my-rewards-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!embedded && (
          <div className="orders-header">
            <div className="orders-header-title-row">
              <Title level={3} style={{ margin: 0 }}>
                My rewards
              </Title>
              <div className="orders-header-actions">
                <Button
                  type="primary"
                  icon={<CheckSquareOutlined />}
                  onClick={() => navigate('/my-tasks')}
                  className="orders-add-btn app-btn-primary"
                >
                  My tasks
                </Button>
                <Button
                  type="primary"
                  icon={<UnorderedListOutlined />}
                  onClick={() => navigate('/my-completions')}
                  className="orders-add-btn app-btn-primary"
                >
                  My completions
                </Button>
              </div>
            </div>
          </div>
        )}

        <Card title="Total rewards by month" loading={summaryLoading} className="my-rewards-summary-card">
          <Space style={{ marginBottom: 16 }}>
            <span>Year:</span>
            <Select
              value={summaryYear}
              onChange={setSummaryYear}
              options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
              style={{ width: 100 }}
            />
          </Space>
          {balance !== null && (
            <p style={{ marginBottom: 16, fontWeight: 600, fontSize: 15 }}>
              Total remaining points: {balance} pts
            </p>
          )}
          {yearTotalDisplay !== 0 || allMonthsForYear.some((m) => m.points !== 0) ? (
            <>
              <p style={{ marginBottom: 16, fontWeight: 600 }}>
                Total for {summaryYear}: {yearTotalDisplay} pts
              </p>
              <div className="my-rewards-summary-grid">
                {allMonthsForYear.map(({ monthKey, monthLabel, displayPoints }) => (
                  <div key={monthKey} className="my-rewards-summary-item">
                    <span className="my-rewards-summary-month">{monthLabel}</span>
                    <span className="my-rewards-summary-points">{displayPoints} pts</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ color: '#64748b', margin: 0 }}>No rewards for this year.</p>
          )}
        </Card>

        <Card title="Reward history">
          <Table
            dataSource={list.map((r) => ({ ...r, key: r._id }))}
            loading={listLoading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (t) => `Total ${t} rewards`,
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={(pag) =>
              setPagination((prev) => ({
                ...prev,
                current: pag.current ?? prev.current,
                pageSize: pag.pageSize ?? prev.pageSize,
              }))
            }
            size="middle"
            columns={[
              {
                title: 'Points',
                dataIndex: 'points',
                key: 'points',
                width: 80,
                render: (val) => <strong>{val}</strong>,
              },
              {
                title: 'Month',
                dataIndex: 'month',
                key: 'month',
                width: 100,
              },
              {
                title: 'Task',
                key: 'task',
                ellipsis: true,
                render: (_, record) => record.taskId?.title ?? '—',
              },
              {
                title: 'Granted at',
                dataIndex: 'grantedAt',
                key: 'grantedAt',
                width: 160,
                render: (val) => (val ? dayjs(val).format('MMM D, YYYY h:mm A') : '—'),
              },
            ]}
          />
        </Card>
      </Space>
    </div>
  );
}
